import React, { ReactNode, useMemo, useState } from "react";

import { Box, Button, styled } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  addWeeks,
  eachDayOfInterval,
  endOfWeek,
  formatISO,
  parseISO,
  startOfWeek,
  subWeeks,
} from "date-fns";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import Boxes from "./Boxes";

const Root = styled("div")(({ theme }) => ({
  padding: theme.spacing(2),
}));

interface DateBoxesProps {
  LinksComponent?: ReactNode;
}

const DateBoxes: React.FC<DateBoxesProps> = ({ LinksComponent }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { date: dateParams } = useParams<{ date: string }>();
  const selectedDate = parseISO(dateParams ?? new Date().toDateString());
  const [, setShowPicker] = useState(false);
  const DATES = useMemo(() => {
    return eachDayOfInterval({
      start: startOfWeek(selectedDate),
      end: endOfWeek(selectedDate),
    });
  }, [dateParams]);

  const navigateTo = (date: Date) => {
    const formattedDate = formatISO(date, { representation: "date" });
    const routeParts = pathname.split("/");
    const [_, clients, clientId, diary, __, exercise] = routeParts;
    const newDate = [_, clients, clientId, diary, formattedDate, exercise].join(
      "/"
    );
    navigate(`${newDate}`);
  };

  const handleDateChange = (date: Date | null) => {
    if (date === null) return;
    setShowPicker(false);
    navigateTo(date);
  };

  const handleNextWeek = () => {
    if (selectedDate === null) return;
    const newDate = addWeeks(selectedDate, 1);
    navigateTo(newDate);
  };
  const handlePrevWeek = () => {
    if (selectedDate === null) return;
    const newDate = subWeeks(selectedDate, 1);
    navigateTo(newDate);
  };

  const handleToday = () => {
    navigateTo(new Date());
  };

  return (
    <>
      <Root>
        <Box marginBottom="12px" display="flex" alignItems="center">
          <DatePicker
            format="dd-MMM-yyyy"
            value={selectedDate}
            onChange={handleDateChange}
          />
          <Box marginRight="8px" />
          <Button onClick={handleToday} size="small" variant="outlined">
            Today
          </Button>
        </Box>
        <Boxes
          dates={DATES}
          onNextWeek={handleNextWeek}
          onPrevWeek={handlePrevWeek}
        />
      </Root>
      {LinksComponent}
    </>
  );
};

export default DateBoxes;
